'use client';

import { c as _c } from "react/compiler-runtime";
import { I18N_CONF } from '@funkey/locales/config';
import { ActivityFiltersLayout } from '~/features/activities/search/client/ActivityFilterBar';
import { SEARCH_ACTIVITY_FILTER_PRESET } from '~/features/activities/search/client/ActivityFilterBar/presets';
import { useI18n } from '~/features/i18n';
import type { StoryblokBlokProps } from './utils';
import type { SB_SearchBlockProps } from './types.generated';
import { createSerializer } from 'nuqs';
import { useMemo } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { cn } from '~/utils';
import { ACTIVITY_FILTER_BAR_QUERY_PARAMS } from '~/features/activities/search/shared';
import { SearchModeToggle } from '~/features/activities/search/SearchModeToggle';
import { SearchModeTabs } from '~/features/activities/search/SearchModeTabs';
import { AiSearchNudge } from '~/features/activities/search/AiSearchNudge';
import { AiChatHistoryButton } from '~/features/activities/search/AiChatHistoryButton';
import { SUGGESTION_DESCRIPTORS } from '~/features/activities/search/ai-search-suggestions';
import { AiSearchBar } from '~/features/activities/search/AiSearchBar';
import { useLocalStorageState } from '~/modules/browser/storage';
import { useIsTailwindBreakpoint } from '~/modules/tailwind/hooks/useIsTailwindBreakpoint';
import { useRouter } from 'next/navigation';

/** Persists the homepage Filters ⇄ AI toggle so a refresh restores the chosen surface. */
const HOME_SEARCH_MODE_KEY = 'funkey:home-search-mode';
export const ActivitySearchBlock: React.FC<StoryblokBlokProps<SB_SearchBlockProps>> = () => {
  const $ = _c(34);
  const state = SEARCH_ACTIVITY_FILTER_PRESET.useFilterState();
  const filterBar = SEARCH_ACTIVITY_FILTER_PRESET.useFilters();
  const i18n = useI18n();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = createSerializer(ACTIVITY_FILTER_BAR_QUERY_PARAMS);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const serializer = t0;
  const {
    state: mode,
    setState: setMode
  } = useLocalStorageState(HOME_SEARCH_MODE_KEY, "normal", "localStorage");
  const isDesktop = useIsTailwindBreakpoint("lg");
  const router = useRouter();
  let t1;
  if ($[1] !== i18n.locale || $[2] !== state.value) {
    t1 = I18N_CONF.getTranslatedUrl("teambuildings", {
      locale: i18n.locale,
      searchParams: serializer(state.value)
    });
    $[1] = i18n.locale;
    $[2] = state.value;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const searchHref = t1;
  let t2;
  if ($[4] !== i18n.locale) {
    t2 = I18N_CONF.getTranslatedUrl("teambuildings", {
      locale: i18n.locale
    });
    $[4] = i18n.locale;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const aiHref = `${t2}?mode=ai`;
  let t3;
  if ($[6] !== mode) {
    t3 = mode === "normal" && <motion.div key="ai-search-nudge" initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 8
    }} transition={{
      duration: 0.3,
      ease: "easeOut"
    }} className="absolute right-[calc(50%_-_12rem)] top-[-2rem] z-10 lg:-left-16 lg:-top-[9rem] lg:right-auto"><AiSearchNudge className="relative left-0 top-0" /></motion.div>;
    $[6] = mode;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t3) {
    t4 = <AnimatePresence>{t3}</AnimatePresence>;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      layout: {
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1]
      }
    };
    t6 = cn("flex w-fit items-center gap-2", "lg:h-[5.5rem] lg:overflow-hidden lg:rounded-5xl lg:bg-white lg:py-2 lg:pl-2 lg:pr-3 lg:shadow-2xl lg:shadow-black/5", "max-lg:w-full max-lg:flex-col max-lg:items-stretch max-lg:gap-3");
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  let t7;
  let t8;
  if ($[12] !== mode || $[13] !== setMode) {
    t7 = <div className="flex justify-center lg:hidden"><SearchModeTabs mode={mode} onChange={setMode} theme="glass" /></div>;
    t8 = mode !== "ai" && <div className="hidden shrink-0 lg:block lg:pl-1"><SearchModeToggle mode={mode} onChange={setMode} theme="light" data-testid="home-search-mode-toggle" /></div>;
    $[12] = mode;
    $[13] = setMode;
    $[14] = t7;
    $[15] = t8;
  } else {
    t7 = $[14];
    t8 = $[15];
  }
  let t9;
  if ($[16] !== aiHref || $[17] !== filterBar || $[18] !== i18n || $[19] !== isDesktop || $[20] !== mode || $[21] !== router || $[22] !== searchHref || $[23] !== setMode || $[24] !== state) {
    t9 = <div className="max-lg:w-full flex items-center"><AnimatePresence mode="popLayout" initial={false}>{mode === "normal" ? <motion.div key="filters" layout={isDesktop} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.18,
          ease: "easeOut"
        }} className="max-lg:w-full lg:w-fit"><ActivityFiltersLayout bare={true} compact={true} state={state} variant="pill" filters={filterBar} button={{
            label: i18n._("Search"),
            href: searchHref,
            iconBefore: "search",
            prefetch: true
          }} /></motion.div> : <motion.div key="ai" layout={isDesktop} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.18,
          ease: "easeOut"
        }} className="max-lg:rounded-3xl max-lg:bg-white max-lg:p-3 max-lg:shadow-2xl max-lg:shadow-black/5 w-full lg:w-[48rem]"><AiSearchBar mode={mode} onModeChange={setMode} showLeadingToggle={isDesktop} href={aiHref} trailing={<AiChatHistoryButton onSelect={id => {
            router.push(`${aiHref}&chat=${encodeURIComponent(id)}`);
          }} />} placeholderPrefix={i18n._("activity.search.ai.placeholderPrefix", undefined, {
            message: "Zoek naar"
          })} suggestions={SUGGESTION_DESCRIPTORS.map(s => i18n._(s.id, undefined, {
            message: s.message
          }))} aiSearchLabel={i18n._("Search")} ariaLabel={i18n._("activity.search.ai.mobileCtaStart", undefined, {
            message: "Zoeken met AI"
          })} /></motion.div>}</AnimatePresence></div>;
    $[16] = aiHref;
    $[17] = filterBar;
    $[18] = i18n;
    $[19] = isDesktop;
    $[20] = mode;
    $[21] = router;
    $[22] = searchHref;
    $[23] = setMode;
    $[24] = state;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== isDesktop || $[27] !== t7 || $[28] !== t8 || $[29] !== t9) {
    t10 = <motion.div layout={isDesktop} transition={t5} className={t6}>{t7}{t8}{t9}</motion.div>;
    $[26] = isDesktop;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== t10 || $[32] !== t4) {
    t11 = <div className="flex w-full justify-center"><div className="max-lg:w-full relative">{t4}{t10}</div></div>;
    $[31] = t10;
    $[32] = t4;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  return t11;
};